import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import React from 'react';
import LiveLabel from '../../Concert/LiveLabel/LiveLabel';
import { font, hovers, mediaQueries, remCalc, sizeMap } from '../../css';
import artePropTypes from '../../propTypes';
import Interchange from '../../UI/Interchange/Interchange';
import RenderLink from '../../UI/RenderLink/RenderLink';
import { StyledProgram as StyledProgramBuf, StyledProgramFigure, stylingProgramLink } from './GuideProgram.css.js';

export const TypographyConcertBase = styled('span')({
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: remCalc(sizeMap.xs * 0.9),
    lineHeight: remCalc(sizeMap.xs * 0.9),
    minHeight: remCalc(sizeMap.xs * 0.9),
    WebkitLineClamp: 2,
    fontWeight: font.weight.regular,
    color: 'white',
    maxWidth: 'inherit',
    [mediaQueries.medium]: {
        fontSize: remCalc(sizeMap.s * 0.9),
        lineHeight: remCalc(sizeMap.s * 0.9),
        minHeight: remCalc(sizeMap.s * 0.9),
    },
});
export const TypographyConcertDate = styled(TypographyConcertBase)({
    textTransform: 'uppercase',
    fontSize: remCalc(sizeMap.xxs * 0.9),
    lineHeight: remCalc(sizeMap.xxs * 0.9),
    minHeight: remCalc(sizeMap.xxs * 0.9),
    [mediaQueries.medium]: {
        fontSize: remCalc(sizeMap.xs * 0.9),
        lineHeight: remCalc(sizeMap.xs * 0.9),
        minHeight: remCalc(sizeMap.xs * 0.9),
    },
});
export const TypographyConcertTitle = styled(TypographyConcertBase)({
    fontSize: remCalc(sizeMap.s * 0.9),
    lineHeight: remCalc(sizeMap.s * 0.9),
    fontWeight: font.weight.bold,
    [mediaQueries.medium]: {
        fontSize: remCalc(sizeMap.m * 0.9),
        lineHeight: remCalc(sizeMap.m * 0.9),
    },
});
export const ProgramConcertTitlesWrapper = styled('div')({
    padding: remCalc(5, 0),
});
export const ProgramConcertWrapper = styled('div')({
    paddingLeft: remCalc(0),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    [mediaQueries.medium]: {
        paddingLeft: remCalc(20),
    },
});
const StyledProgramLink = stylingProgramLink(RenderLink);

const StyledProgram = styled(StyledProgramBuf)({
    '&:first-of-type a': {
        border: 'none',
    },
});

const getLabel = (isLive, labelType, labels, concertLabels) => {
    if (isLive) return labels.live;
    switch (labelType) {
        case 'LIVESTREAM_TV':
            return concertLabels.onAir;
        case 'LIVESTREAM_WEB':
            return concertLabels.livestream;
        default:
            return '';
    }
};
const ConcertGuideProgram = props => {
    const {
        concertLabels,
        hidden,
        images,
        labels,
        labelType,
        mouseFocus,
        renderLink,
        stickers,
        subtitle,
        tabIndex,
        title,
        url,
    } = props;
    const isLive = (stickers && stickers.find(el => el.code === 'LIVE')) || false;
    const label = getLabel(isLive, labelType, labels, concertLabels);

    return (
        <StyledProgram>
            <StyledProgramLink
                aria-hidden={hidden}
                href={url}
                tabIndex={tabIndex}
                renderLink={renderLink}
                mouseFocus={mouseFocus}
            >
                <StyledProgramFigure>
                    <Interchange
                        images={images}
                        title={title}
                        breakpointFormat={artePropTypes.breakpointFormat}
                        hover={hovers.basic}
                        ariaOptions={{
                            role: 'presentation',
                        }}
                        noFocus
                    />
                </StyledProgramFigure>
                <ProgramConcertWrapper>
                    <TypographyConcertDate>
                        {labels.longDate}
                        {' | '}
                        {labels.hour}
                    </TypographyConcertDate>
                    <ProgramConcertTitlesWrapper>
                        <TypographyConcertTitle title={title}>{title}</TypographyConcertTitle>
                        <TypographyConcertBase title={subtitle}>{subtitle}</TypographyConcertBase>
                    </ProgramConcertTitlesWrapper>
                    <span>{label && <LiveLabel size="medium" label={label} live={isLive} />}</span>
                </ProgramConcertWrapper>
            </StyledProgramLink>
        </StyledProgram>
    );
};

ConcertGuideProgram.propTypes = {
    concertLabels: PropTypes.shape({
        onAir: PropTypes.string,
        livestream: PropTypes.string,
    }),
    labelType: PropTypes.oneOf(['LIVESTREAM_WEB', 'LIVESTREAM_TV']),
    hidden: PropTypes.bool,
    images: PropTypes.arrayOf(artePropTypes.image),
    labels: PropTypes.shape({
        hour: PropTypes.string.isRequired,
        live: PropTypes.string.isRequired,
        shortDate: PropTypes.string.isRequired,
        longDate: PropTypes.string,
    }).isRequired,
    mouseFocus: PropTypes.bool,
    renderLink: PropTypes.func,
    stickers: PropTypes.arrayOf(artePropTypes.stickers),
    subtitle: PropTypes.string,
    tabIndex: PropTypes.number,
    title: PropTypes.string.isRequired,
    url: PropTypes.string,
};

ConcertGuideProgram.defaultProps = {
    hidden: false,
    labels: {
        hour: '',
        live: 'live',
        longDate: '',
        shortDate: '',
    },
    concertLabels: {
        onAir: '',
        livestream: '',
    },
    images: [],
    mouseFocus: false,
    title: '',
    url: '#',
};

export default ConcertGuideProgram;
